import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormHelperText, TextField, Typography } from "@mui/material";
import Divider from "@mui/material/Divider";
import DocumentUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useTranslation } from "react-i18next";
import { getUploadEntryName } from "../attachmentUtils";
import { rejectSchema } from "./rejectSchema.ts";

// This form does not have a store defined, so all fields will be managed via local state or props
// => use regular input components and manage their state locally or via props
interface RejectVesselCallDrawerProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  onDocumentUpload?: (file: File) => void;
  onAdditionalDocumentUpload?: (file: File) => void;
  onSubmit?: (payload?: any) => void;
}

const RejectVesselCallDrawer: React.FC<RejectVesselCallDrawerProps> = ({
  isOpen,
  onClose,
  onDocumentUpload,
  onAdditionalDocumentUpload,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const LANG_BASE = "vessel-call";
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };
  const TEXTAREA_MAX_CHARS = 1000;
  const externalUploadSignatureRef = useRef<string | null>(null);
  const internalUploadSignatureRef = useRef<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

  const rejectionOnlySchema = rejectSchema(t).pick({ rejectionReason: true });
  const internalOnlySchema = rejectSchema(t).pick({ internalNotes: true });
  const [errors, setErrors] = useState<{
    rejectionReason?: string;
    internalNotes?: string;
    rejectionFile?: string;
    internalFile?: string;
  }>({});
  const fileProfiles = useFileUploadProfiles();
  const rejectionProfile = fileProfiles.sideDrawerApprovalFile;
  const internalProfile = rejectionProfile;

  const rejectionFiles = useUploadStore(
    (s) => s.filesByKey.rejectionAttachment ?? EMPTY_UPLOAD_ENTRIES,
  );
  const internalFiles = useUploadStore(
    (s) => s.filesByKey.rejectionInternalAttachment ?? EMPTY_UPLOAD_ENTRIES,
  );
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const allowedRejectionExtensions = rejectionProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );
  const allowedInternalExtensions = internalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const clearFilesAndNotes = useCallback(() => {
    removeInternalFile();
    removeRejectionFile();
    setRejectionReason("");
    setInternalNotes("");
  }, [clearFilesForKey]);

  useEffect(() => {
    clearFilesAndNotes();
    setErrors({});
  }, [clearFilesAndNotes]);

  const handleClose = () => {
    onClose("rejectDrawer");
  };

  const handleCancel = () => {
    clearFilesAndNotes();
    setErrors({});
    onClose("rejectDrawer");
  };

  const setRejectionAttachment = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("rejectionAttachment");
        return;
      }
      setFilesForKey("rejectionAttachment", files);
    },
    [clearFilesForKey, setFilesForKey],
  );
  const setRejectionInternalAttachment = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("rejectionInternalAttachment");
        return;
      }
      setFilesForKey("rejectionInternalAttachment", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.MutableRefObject<string | null>,
    ) => {
      if (!handler) return;
      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);
      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
  }: {
    newFiles: File[];
    setUploadErrors: (
      value:
        | { rejectionFile?: string; internalFile?: string }
        | ((prev: { rejectionFile?: string; internalFile?: string }) => {
            rejectionFile?: string;
            internalFile?: string;
          }),
    ) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: React.MutableRefObject<string | null>;
  }) => {
    if (newFiles.length > 1) {
      setUploadErrors({ rejectionFile: t("upload.one-document") });
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setUploadErrors((prev) => ({ ...prev, files: undefined }));
    setUploadFiles([file]);
    triggerUpload(file, onUploadDocument, uploadTracker);
  };

  const removeInternalFile = () => {
    clearFilesForKey("rejectionInternalAttachment");
  };
  const removeRejectionFile = () => {
    clearFilesForKey("rejectionAttachment");
  };

  const handleSubmit = () => {
    const toValidate = {
      files: undefined,
      internalNotes,
      rejectionReason,
    };

    const validation = rejectSchema(t).safeParse(toValidate);
    if (!validation.success) {
      const flat = validation.error.flatten().fieldErrors;
      setErrors((prev) => ({
        ...prev,
        rejectionReason: flat.rejectionReason?.[0],
        internalNotes: flat.internalNotes?.[0],
      }));
      return;
    }

    const hasRejectionAttachment = rejectionFiles.length > 0;
    const hasInternalAttachment = internalFiles.length > 0;

    const payload = {
      rejectionReason,
      internalNotes,
      hasRejectionAttachment,
      hasInternalAttachment,
      rejectionAttachmentName: hasRejectionAttachment
        ? getUploadEntryName(rejectionFiles[0]) || undefined
        : undefined,
      internalAttachmentName: hasInternalAttachment
        ? getUploadEntryName(internalFiles[0]) || undefined
        : undefined,
      status: "REJECTED",
    };
    onSubmit?.(payload);
    clearFilesAndNotes();
    onClose("rejectDrawer");
  };

  return (
    <div data-testid="reject-vessel-call" className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        title={t(`${LANG_BASE}.reject.title`)}
        onClose={handleClose}
        onCancel={handleCancel}
        submitLabel={t(`${LANG_BASE}.reject.reject-button`)}
        onSubmit={handleSubmit}
      >
        <Typography
          variant="body1"
          data-testid="reject-vessel-call-title"
          sx={{ color: "text.primary", px: 3, pb: 3 }}
        >
          {t(`${LANG_BASE}.reject.subtitle`)}
        </Typography>
        <div className="flex-1 w-[400px] py-2">
          <div className="flex flex-col gap-4 px-6">
            {/* Rejection Reason */}
            <div className="flex flex-col gap-2 " data-testid="reject-reason">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(`${LANG_BASE}.common.external-notes`)}
              </Typography>
              <TextField
                multiline
                minRows={4}
                value={rejectionReason}
                placeholder={t(`${LANG_BASE}.common.external-notes`)}
                sx={{ "& textarea": { padding: "12px", borderRadius: "8px" } }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (!isPrintableKey(e)) return;
                  if (rejectionReason.length >= TEXTAREA_MAX_CHARS) {
                    setErrors((prev) => ({
                      ...prev,
                      rejectionReason: t("form-validation.max-length", {
                        label: t(`${LANG_BASE}.common.external-notes`),
                        max: TEXTAREA_MAX_CHARS,
                      }),
                    }));
                  }
                }}
                onBlur={() => {
                  const res = rejectionOnlySchema.safeParse({ rejectionReason });
                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({ ...prev, rejectionReason: flat.rejectionReason?.[0] }));
                  } else {
                    setErrors((prev) => ({ ...prev, rejectionReason: undefined }));
                  }
                }}
                onChange={(e) => {
                  const val = e.target.value;
                  setRejectionReason(val);
                  const res = rejectionOnlySchema.safeParse({ rejectionReason: val });
                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({ ...prev, rejectionReason: flat.rejectionReason?.[0] }));
                  } else {
                    setErrors((prev) => ({ ...prev, rejectionReason: undefined }));
                  }
                }}
              />
              {errors.rejectionReason && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.rejectionReason}
                </FormHelperText>
              )}
              <FormHelperText sx={charCounterSx}>
                {rejectionReason.length}/{TEXTAREA_MAX_CHARS}
              </FormHelperText>
              <DocumentUpload
                label={t(`${LANG_BASE}.common.external-attachments`)}
                testId="reject-external-attachment-upload"
                name="rejectionAttachment"
                maxFiles={1}
                allowedExtensions={allowedRejectionExtensions}
                onUpload={(newFiles) =>
                  handleFileUpload({
                    newFiles,
                    setUploadErrors: setErrors,
                    setUploadFiles: setRejectionAttachment,
                    onUploadDocument: onDocumentUpload,
                    uploadTracker: externalUploadSignatureRef,
                  })
                }
                conditions={rejectionProfile}
                files={rejectionFiles}
                setFiles={(next) => setFilesForKey("rejectionAttachment", next)}
                uploadDescription={t("upload.dragOrDropDescription")}
              />
              {errors.rejectionFile && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.rejectionFile}
                </FormHelperText>
              )}
              {rejectionFiles[0] && (
                <SingleFile
                  file={rejectionFiles[0]}
                  fieldName="rejectionAttachment"
                  setFiles={() => {
                    setErrors((prev) => ({ ...prev, files: undefined }));
                    setRejectionAttachment([]);
                    externalUploadSignatureRef.current = null;
                  }}
                />
              )}
            </div>
            <Divider />
            {/* Internal Notes */}
            <div className="flex flex-col gap-2" data-testid="reject-internal-notes">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(`${LANG_BASE}.common.internal-notes`)}
              </Typography>
              <TextField
                multiline
                minRows={4}
                value={internalNotes}
                placeholder={t(`${LANG_BASE}.common.internal-notes`)}
                sx={{ "& textarea": { padding: "12px", borderRadius: "8px" } }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (!isPrintableKey(e)) return;
                  if (internalNotes.length >= TEXTAREA_MAX_CHARS) {
                    setErrors((prev) => ({
                      ...prev,
                      internalNotes: t("form-validation.max-length", {
                        label: t(`${LANG_BASE}.common.internal-notes`),
                        max: TEXTAREA_MAX_CHARS,
                      }),
                    }));
                  }
                }}
                onBlur={() => {
                  const res = internalOnlySchema.safeParse({ internalNotes });
                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({ ...prev, internalNotes: flat.internalNotes?.[0] }));
                  } else {
                    setErrors((prev) => ({ ...prev, internalNotes: undefined }));
                  }
                }}
                onChange={(e) => {
                  const val = e.target.value;
                  setInternalNotes(val);
                  const res = internalOnlySchema.safeParse({ internalNotes: val });
                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({ ...prev, internalNotes: flat.internalNotes?.[0] }));
                  } else {
                    setErrors((prev) => ({ ...prev, internalNotes: undefined }));
                  }
                }}
              />
              {errors.internalNotes && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.internalNotes}
                </FormHelperText>
              )}
              <FormHelperText sx={charCounterSx}>
                {internalNotes.length}/{TEXTAREA_MAX_CHARS}
              </FormHelperText>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(`${LANG_BASE}.common.internal-attachments`)}
              </Typography>
              <DocumentUpload
                testId="reject-internal-attachment-upload"
                onUpload={(newFiles) =>
                  handleFileUpload({
                    newFiles,
                    setUploadErrors: setErrors,
                    setUploadFiles: setRejectionInternalAttachment,
                    onUploadDocument: onAdditionalDocumentUpload,
                    uploadTracker: internalUploadSignatureRef,
                  })
                }
                name="rejectionInternalAttachment"
                conditions={internalProfile}
                files={internalFiles}
                setFiles={(next) => setFilesForKey("rejectionInternalAttachment", next)}
                maxFiles={1}
                allowedExtensions={allowedInternalExtensions}
                uploadDescription={t("upload.dragOrDropDescription")}
              />
              {errors.internalFile && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.internalFile}
                </FormHelperText>
              )}
              {internalFiles[0] && (
                <SingleFile
                  file={internalFiles[0]}
                  testId="reject-internal-attachment"
                  fieldName="rejectionInternalAttachment"
                  currentFileIndex={0}
                  setFiles={() => {
                    setErrors((prev) => ({ ...prev, files: undefined }));
                    setRejectionInternalAttachment([]);
                    internalUploadSignatureRef.current = null;
                  }}
                  removeFn={removeInternalFile}
                />
              )}
            </div>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default RejectVesselCallDrawer;
